import React, { ReactNode } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import Divider from "src/components/Divider";

export type BaseModalProps = {
  onClose: () => void;
  title: string; // 제목
  subtitle?: string; // 부제목
  lines?: string[]; // 본문
  caption?: string; // 캡션
  confirmText?: string; // 버튼 텍스트
  editText?: string; // 수정 버튼 텍스트
  onConfirm?: () => void; // 확인 버튼 클릭 시 동작
  children?: ReactNode; // 자식 컴포넌트
};

const BaseModal: React.FC<BaseModalProps> = ({
  onClose,
  title,
  subtitle,
  lines = [],
  caption,
  confirmText = "",
  editText = "",
  onConfirm,
  children,
}) => {
  return createPortal(
    <ModalOverlay role="dialog" aria-modal="true" aria-labelledby="modal-title" onClick={onClose}>
      {/* 카드 */}
      <ModalCard onClick={(e) => e.stopPropagation()}>
        {/* 제목 */}
        <ModalHeader>
          <ModalTitle id="modal-title">{title}</ModalTitle>
        </ModalHeader>

        <Divider />

        {/* 부제목 */}
        {subtitle && (
          <div>
            <ModalSubtitle>{subtitle}</ModalSubtitle>
          </div>
        )}

        {/* 본문 */}
        <ModalContent>
          {lines.map((line, idx) => (
            <ModalText key={idx}>{line}</ModalText>
          ))}
        </ModalContent>

        {/* 캡션 */}
        {caption && (
          <ModalContent>
            <CaptionText>{caption}</CaptionText>
          </ModalContent>
        )}

        {/* 확인 버튼 */}
        {children}
      </ModalCard>
    </ModalOverlay>,
    document.body
  );
};

// Styled Components
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  z-index: 11000001;
`;

const ModalCard = styled.div`
  display: flex;
  padding-top: 24px;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  width: 90%;
  max-width: 400px;
  border-radius: 16px;
  background-color: white;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
`;

const ModalHeader = styled.div`
  padding: 0 24px;
`;

const ModalTitle = styled.h2`
  color: #1a1a1a;
  font-family: Pretendard, sans-serif;
  font-size: 18px;
  font-weight: 700;
  line-height: 24px;
  margin: 0;
`;

const ModalSubtitle = styled.p`
  color: #1a1a1a;
  font-family: Pretendard, sans-serif;
  font-size: 14px;
  font-weight: 700;
  line-height: 18px;
  padding: 0 24px;

  margin: 0;
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 24px;
`;

const ModalText = styled.p`
  color: #1a1a1a;
  font-family: Pretendard, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 150%;
  margin: 0;
`;

const CaptionText = styled.p`
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 16px;
  color: rgb(102, 102, 102);
`;

const Backdrop = styled.div`
  position: absolute;
  inset: 0;
  z-index: -10;
`;

export default BaseModal;
